import { IScrappedExamGroupFilter } from '@sdk/contracts';
import { useQuery } from '@tanstack/react-query';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const INITIAL_PAGE = 1;
const INITIAL_LIMIT = 50;

const ScrappedExamGroupService = sdk.getService('ScrappedExamGroupService');

interface IStore {
  page: number;
  limit: number;
  search: string;
  filter: IScrappedExamGroupFilter;
  setFilter: (filter: IScrappedExamGroupFilter) => void;
  setSearch: (search: string) => void;
  setPageAndLimit: (page: number, limit: number) => void;
}

const store = createStore<IStore>(set => ({
  page: INITIAL_PAGE,
  limit: INITIAL_LIMIT,
  search: '',
  filter: { onlyStateExams: false },
  setFilter: (filter: IScrappedExamGroupFilter) =>
    set(state => ({ ...state, page: INITIAL_PAGE, filter })),
  setSearch: (search: string) => set(state => ({ ...state, search, page: INITIAL_PAGE })),
  setPageAndLimit: (page: number, limit: number) => set(state => ({ ...state, page, limit }))
}));

export const useScrappedExamGroupsList = () => {
  const { page, limit, search, filter, setPageAndLimit, setSearch, setFilter } = store();

  const queryKey = ['exam-groups', 'scrapped', `page-${page}`, `limit-${limit}`, search, filter];

  const { isLoading, data, error } = useQuery({
    queryKey,
    queryFn: () => ScrappedExamGroupService.getScrappedExamGroups(search, { page, limit }, filter)
  });

  const pagination = data && {
    page: data.page,
    limit: data.limit,
    hasNextPage: data.hasNextPage,
    hasPrevPage: data.hasPrevPage,
    nextPage: data.nextPage,
    pagingCounter: data.pagingCounter,
    prevPage: data.prevPage,
    totalDocs: data.totalDocs,
    totalPages: data.totalPages
  };

  const resetSearchResults = () => setSearch('');

  return {
    scrappedExamGroups: data?.docs,
    isLoading,
    pagination,
    error,
    setPageAndLimit,
    search,
    filter,
    setFilter,
    setSearch,
    resetSearchResults
  };
};
